import type { SystemAnnouncement, CreateAnnouncementRequest } from '../types/announcement';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

class AnnouncementService {
  private getToken(): string | null {
    return localStorage.getItem('token');
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (options.headers) Object.assign(headers, options.headers as Record<string, string>);

    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Session expired');
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(err.detail || 'Request failed');
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  /** Public — no auth needed. Uses plain fetch to avoid sending stale tokens. */
  async getActive(): Promise<SystemAnnouncement[]> {
    const response = await fetch(`${API_URL}/api/v1/announcements/active`);
    if (!response.ok) return [];
    return response.json();
  }

  // Admin endpoints
  async adminList(): Promise<SystemAnnouncement[]> {
    return this.request('/api/v1/admin/announcements');
  }

  async adminCreate(data: CreateAnnouncementRequest): Promise<SystemAnnouncement> {
    return this.request('/api/v1/admin/announcements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async adminDeactivate(announcementId: number): Promise<SystemAnnouncement> {
    return this.request(`/api/v1/admin/announcements/${announcementId}`, {
      method: 'PATCH',
    });
  }
}

export const announcementService = new AnnouncementService();
